import { Fragment } from 'react';

/**
 * Lightweight, dependency-free renderer for help-article bodies (item 7.6).
 *
 * WHY a tiny custom renderer rather than a Markdown library: the Help Guide ships
 * inside a strict CSP with zero external requests, and the article bodies only need
 * a handful of block shapes to read well — headings, numbered steps, bullet lists
 * and paragraphs, with inline emphasis. Pulling in a full Markdown/AST dependency
 * for that would add weight and a sanitising surface for no benefit. Instead each
 * entry in {@link HelpArticle.body} is treated as one *block* and classified by a
 * cheap prefix test:
 *
 * - `## Heading`  → a section heading.
 * - lines each starting `- ` → an unordered list.
 * - lines each starting `1. ` (any number) → an ordered list (the reader sees the
 *   real step numbers, so numbered runbooks read as steps).
 * - anything else → a paragraph.
 *
 * Inline `**bold**` is supported everywhere for emphasising field names, states and
 * "gotcha" callouts. No raw HTML is ever interpreted, so the content stays safe and
 * theme-aware. Plain-string bodies (the legacy shape) still render as paragraphs, so
 * this is fully backwards-compatible with older articles.
 */
export interface HelpArticleBodyProps {
  /** The article body blocks, rendered in order. */
  body: ReadonlyArray<string>;
}

/** Split a text run on `**bold**` spans and render the emphasised parts. */
function renderInline(text: string): JSX.Element {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
          <strong key={index} className="font-semibold text-slate-900 dark:text-slate-100">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}

/** Non-empty lines of a block, trimmed of trailing whitespace. */
function lines(block: string): string[] {
  return block.split('\n').map((line) => line.trimEnd()).filter((line) => line.trim() !== '');
}

/** True when every non-empty line begins with an unordered-list marker. */
function isBulletList(block: string): boolean {
  const rows = lines(block);
  return rows.length > 0 && rows.every((row) => /^-\s+/.test(row.trimStart()));
}

/** True when every non-empty line begins with an ordered-list marker (`1. `). */
function isOrderedList(block: string): boolean {
  const rows = lines(block);
  return rows.length > 0 && rows.every((row) => /^\d+\.\s+/.test(row.trimStart()));
}

/** Render a single body block into the right element. */
function Block({ block }: { block: string }): JSX.Element {
  const trimmed = block.trimStart();

  if (trimmed.startsWith('## ')) {
    return (
      <h3 className="mt-4 text-sm font-semibold uppercase tracking-wide text-slate-900 first:mt-0 dark:text-slate-100">
        {renderInline(trimmed.slice(3).trim())}
      </h3>
    );
  }

  if (isBulletList(block)) {
    return (
      <ul className="ml-1 list-disc space-y-1 pl-4 marker:text-slate-400 dark:marker:text-slate-500">
        {lines(block).map((row, index) => (
          <li key={index}>{renderInline(row.trimStart().replace(/^-\s+/, ''))}</li>
        ))}
      </ul>
    );
  }

  if (isOrderedList(block)) {
    return (
      <ol className="ml-1 list-decimal space-y-1 pl-5 marker:text-slate-400 dark:marker:text-slate-500">
        {lines(block).map((row, index) => (
          <li key={index}>{renderInline(row.trimStart().replace(/^\d+\.\s+/, ''))}</li>
        ))}
      </ol>
    );
  }

  return <p>{renderInline(block)}</p>;
}

/**
 * Render an article body: a stack of classified blocks with consistent spacing that
 * works in both themes.
 */
export function HelpArticleBody({ body }: HelpArticleBodyProps): JSX.Element {
  return (
    <div className="space-y-3">
      {body.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}
